import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Image } from '../../../../modules/design-system';
import type { PoolOfferListItem } from '../../../../modules/api';
import { useLobbyPedestal } from '../../../sleek/Pack/Slider/useLobbyPedestal';

export interface SliderProps {
  offers: PoolOfferListItem[];
  activeOfferId: number | null;
  onSlideChange?: (offerId: number) => void;
  /** Height (px) the phone layout leaves the slider; `null` on the desktop. */
  phoneRoom?: number | null;
}

/** Max slides rendered on each side of the active one. Only one peek
    per side — Figma 543:2118 shows exactly one neighbour visible left
    and right of the centre pack. The `+ 0.5` cull margin (in the render
    loop) keeps the incoming peek mounted while the scroll sits between two
    packs. */
const MAX_RENDER_WINDOW = 1;
/** Debounce (ms) after the last scroll event before the slider is treated
    as "settled" on a pack and reported via `onSlideChange`. */
const SETTLE_DEBOUNCE_MS = 120;
/** Desktop: minimum horizontal sliver (px) of a peek pack that must stay
    visible inside the slider container. The Figma `slotStep` is tuned for
    wide desktops; on 900–1300px viewports the left column shrinks and a
    fixed step pushes the left peek fully off-screen — the step is capped
    so at least this much of the peek always shows. */
const MIN_PEEK_VISIBLE_PX = 90;

/** The side arrows: glass circles 36 across (repackz-handoff §8.1). */
const CHEVRON_SIZE_PX = 36;

/** Bundled plinth for a pack without its own `pedestalImageUrl` (modern's). */
const DEFAULT_PEDESTAL_URL = '/templates/modern/default-piedestal.png';

type Tier = 'mobile' | 'tablet' | 'desktop';

type Pedestal = { w: number; h: number; top: number; peekScale: number };

/**
 * The plinth under the pack — off by default (the Repackz design stands the
 * pack on nothing); the operator turns it on with the `Pedestal` knob. On
 * the desktop modern's plinth sits right under the 495-tall pack; on a phone
 * it follows the grown pack. The slider grows to fit it.
 */
const DESKTOP_PEDESTAL: Pedestal = { w: 250, h: 85, top: 535, peekScale: 0.68 };

function phonePedestal(pack: { w: number; h: number; top: number }): Pedestal {
  return {
    w: Math.round(pack.w * 1.15),
    h: Math.round(pack.w * 0.36),
    top: pack.top + pack.h - Math.round(pack.w * 0.06),
    peekScale: 0.6,
  };
}

/**
 * Pixel geometry of the phone and desktop coverflows (repackz-handoff §8).
 * `slotStep` is the horizontal distance (px) between the centre pack and
 * its immediate peek — the base the live step is fitted from.
 */
interface Geometry {
  sliderHeight: number;
  pack: { w: number; h: number; top: number };
  slotStep: number;
  packPeekScale: number;
  /** Opacity of a neighbouring pack at rest. */
  packPeekOpacity: number;
  /** Centre of the side arrows, measured from the pack's centre. */
  chevronOffset: number;
}

// Glass phone (repackz-handoff §8.2): a 132×194 pack, neighbours at 0.82
// 176 apart. It grows with the screen (see `phoneScale`); the title is
// always text, set under the slider.
const PHONE: Geometry = {
  sliderHeight: 210,
  pack:     { w: 132, h: 194, top: 8 },
  slotStep: 176,
  packPeekScale:     0.82,
  packPeekOpacity:   0.55,
  chevronOffset:     0,                   // no arrows on phones
};

/** Most a phone pack grows (repackz-handoff §8.2). */
const PHONE_MAX_SCALE = 2.1;
/** Width kept clear beside a grown phone pack for the neighbours. */
const PHONE_SIDE_ROOM_PX = 104;
/** How much of a neighbour stays on a phone screen (repackz-handoff §8.2). */
const PHONE_PEEK_VISIBLE_PX = 65;
/** The one-line title the slider sets under itself on a phone. */
const PHONE_TITLE_PX = 30;

/** How much the phone pack grows: as far as the height left to the slider
 *  and its title allows, capped by the width and by `PHONE_MAX_SCALE`,
 *  never below its base size (a short screen scrolls instead). */
function phoneScale(room: number | null, containerW: number): number {
  if (room === null || containerW <= 0) return 1;
  const byHeight = (room - PHONE_TITLE_PX) / PHONE.sliderHeight;
  const byWidth = (containerW - PHONE_SIDE_ROOM_PX) / PHONE.pack.w;
  return Math.max(1, Math.min(byHeight, byWidth, PHONE_MAX_SCALE));
}

function scaled(geometry: Geometry, k: number): Geometry {
  if (k === 1) return geometry;
  const px = (v: number) => Math.round(v * k);
  return {
    ...geometry,
    sliderHeight: px(geometry.sliderHeight),
    pack: { w: px(geometry.pack.w), h: px(geometry.pack.h), top: px(geometry.pack.top) },
    slotStep: px(geometry.slotStep),
  };
}

// Glass desktop (Figma 19074:253956, repackz-handoff §8.1): the pack at its
// Figma 336×495 with no plinth, neighbours at 0.76 and 0.3 opacity on the
// same vertical centre, 470 apart, arrows 324 from the pack's centre.
const DESKTOP: Geometry = {
  sliderHeight: 560,
  pack:     { w: 336, h: 495, top:  32 },
  slotStep: 470,
  packPeekScale:     0.76,
  packPeekOpacity:   0.3,
  chevronOffset:     324,
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Coverflow slider of the glass storefront.
 *
 *   • viewport < 800 — the phone pack, grown to the height the layout
 *     leaves it (`phoneRoom`), its title set underneath.
 *   • viewport ≥ 800 — the desktop pack at its Figma 336×495.
 *
 * Each slot is absolutely positioned at the slider's horizontal centre and
 * translated sideways by `distance × slotStep`. Inside it the pack (and the
 * optional pedestal) scale and fade with the slot's `centeredness` — a
 * fractional value that updates in real time while scrolling, so peeks
 * shrink as they move away from the centre.
 *
 * Input is a NATIVE horizontal scroll container (an invisible layer on top
 * of the coverflow), so the browser owns the gesture — iOS snap, momentum
 * and vertical-vs-horizontal axis locking come for free, and a horizontal
 * swipe can't leak into a parent page's scroll
 * (`overscroll-behavior-x: contain`). The visual coverflow is driven by the
 * scroll position; once it settles on a pack we fire `onSlideChange` and the
 * parent updates `gameId` in-state without touching the URL.
 */
export function Slider({
  offers,
  activeOfferId,
  onSlideChange,
  phoneRoom = null,
}: SliderProps) {

  const len = offers.length;
  const externalIndex =
    activeOfferId !== null && len > 0
      ? Math.max(0, offers.findIndex((o) => o.id === activeOfferId))
      : 0;
  const offersKey = offers.map((o) => o.id).join(',');

  // Fractional pack position read from the native scroll layer: 0 = first
  // pack centred, 1.5 = halfway between the 2nd and 3rd. Drives the coverflow
  // geometry every frame so peeks scale/slide smoothly while scrolling.
  const [scrollPos, setScrollPos] = useState(externalIndex);
  const [tier, setTier] = useState<Tier>('mobile');
  const hasPedestal = useLobbyPedestal();
  const [containerW, setContainerW] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last index reported to the parent — guards against re-emitting and against
  // echoing the parent's own `activeOfferId` update back as a change.
  const reportedIndex = useRef(externalIndex);
  // Desktop drag-to-scroll: snap is turned OFF while dragging with a mouse so
  // the content tracks the cursor instead of snapping to points mid-drag, then
  // turned back on (and snapped) on release. Touch never flips this.
  const [snapEnabled, setSnapEnabled] = useState(true);
  // While a desktop mouse drag is active, holds a fn that tears down its
  // window listeners (also called on unmount). Non-null == drag in progress.
  const dragCleanupRef = useRef<(() => void) | null>(null);
  // True while a FINGER is down on the scroll layer. Touch events are the
  // reliable signal here — iOS fires `pointercancel` the moment a touch turns
  // into a scroll, so a pointer-based flag would read "released" halfway
  // through the swipe. A touch's `touchend` always targets the element the
  // touch STARTED on, so element handlers hear the release even if the finger
  // wandered off the slider.
  const touchActive = useRef(false);
  // Last index/step/list the positioning effect applied — lets it tell an
  // external pack switch (animate) from a first mount, a resize/tier change or
  // a new offers list (jump).
  const applied = useRef({ index: externalIndex, slotStep: 0, offersKey, mounted: false });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cancel any pending rAF / settle timer on unmount.
  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (settleTimer.current) clearTimeout(settleTimer.current);
      dragCleanupRef.current?.();
    },
    [],
  );

  useEffect(() => {
    // Tier follows the LAYOUT width — `documentElement.clientWidth`, the
    // exact measure the pack grid's `@[600px]`/`@[800px]` container queries
    // use (scrollbar EXCLUDED). A viewport media query counts the scrollbar
    // in, so in the ~800–816px window band the slider would flip to the
    // desktop tier (no pedestal title — PackInfoCard owns it there) while
    // the grid is still mobile and keeps PackInfoCard hidden, leaving the
    // pack with no title at all.
    const update = () => {
      const w = document.documentElement.clientWidth;
      setTier(w >= 800 ? 'desktop' : w >= 600 ? 'tablet' : 'mobile');
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, []);

  const isDesktop = tier === 'desktop';
  const dims = isDesktop ? DESKTOP : scaled(PHONE, phoneScale(phoneRoom, containerW));

  // Peek distance adapts to the actual container width. Desktop: the Figma
  // step applies as long as it leaves MIN_PEEK_VISIBLE_PX of the peek inside
  // the container; narrower columns pull the peeks in so the left one never
  // vanishes. Phone: the neighbour always shows PHONE_PEEK_VISIBLE_PX, however
  // far the centre pack grew. A peek meeting the centre pack slides behind it
  // (peek slots have lower z-index), which reads as coverflow depth.
  const peekVisualHalfW = (dims.pack.w * dims.packPeekScale) / 2;
  const slotStep =
    containerW > 0
      ? isDesktop
        ? Math.min(dims.slotStep, containerW / 2 + peekVisualHalfW - MIN_PEEK_VISIBLE_PX)
        : containerW / 2 + peekVisualHalfW - PHONE_PEEK_VISIBLE_PX
      : dims.slotStep;

  // Left/right spacer so the first and last pack can snap to the CENTRE of
  // the viewport: a snap point sits every `slotStep`, so `scrollLeft = i*step`
  // centres pack i and `scrollPos = scrollLeft / slotStep` is a clean index.
  const sidePad = Math.max(0, (containerW - slotStep) / 2);

  const clampIndex = (i: number) => Math.min(len - 1, Math.max(0, i));
  const currentIndex = clampIndex(Math.round(scrollPos));

  const scrollToIndex = (index: number, smooth: boolean) => {
    const el = scrollRef.current;
    if (!el || slotStep <= 0) return;
    const left = clampIndex(index) * slotStep;
    if (smooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ left, behavior: 'smooth' });
    } else {
      el.scrollLeft = left;
    }
  };

  // Park the scroll layer on the active pack. ANIMATE when the pack changed
  // via an external switch (parent / PackInfoCard tier) with the layout
  // unchanged — so it slides like the pagination dots. JUMP instantly on the
  // first mount, on slotStep changes (resize / tier flip) and when the offers
  // list itself changes, so nothing slides on load or reflow. A deep link
  // first renders the lone detail pack at index 0; when the category list
  // lands the same pack sits at another index, and animating there would
  // show the first pack and slide back. The `> 1` guard skips redundant work
  // when the scroll is already parked on the target (e.g. right after a user
  // swipe). A layout effect, so the jump lands before the browser paints — a
  // passive one let the new list paint a frame at the OLD scroll position.
  useLayoutEffect(() => {
    const target = Math.min(len - 1, Math.max(0, externalIndex));
    reportedIndex.current = target;
    const prev = applied.current;
    const smooth =
      prev.mounted &&
      target !== prev.index &&
      prev.slotStep === slotStep &&
      prev.offersKey === offersKey;
    applied.current = { index: target, slotStep, offersKey, mounted: true };

    const el = scrollRef.current;
    if (el && slotStep > 0) {
      const left = target * slotStep;
      if (Math.abs(el.scrollLeft - left) > 1) {
        if (smooth && typeof el.scrollTo === 'function') {
          // onScroll drives `scrollPos` frame-by-frame during the animation,
          // so we must NOT also set it here (that would snap-then-slide).
          el.scrollTo({ left, behavior: 'smooth' });
          return;
        }
        el.scrollLeft = left;
      }
    }
    setScrollPos(target);
  }, [externalIndex, slotStep, len, offersKey]);

  // Report the pack the scroll came to rest on. Gestures in progress are
  // skipped: a mouse drag reports on release (via `finish`), and a touch swipe
  // reports on `touchend`. Reporting mid-gesture used to hand the parent a pack
  // the finger was merely PASSING OVER — the parent then set `activeOfferId`,
  // which fired the positioning effect's smooth `scrollTo` into the live
  // gesture AND stamped `reportedIndex`, so the pack the user actually released
  // on could match it and never be reported at all. Slow swipes (which pause
  // longer than the debounce mid-drag) hit that every time.
  const reportSettled = () => {
    if (dragCleanupRef.current || touchActive.current) return;
    const node = scrollRef.current;
    if (!node || slotStep <= 0) return;
    const landed = clampIndex(Math.round(node.scrollLeft / slotStep));
    if (landed !== reportedIndex.current) {
      reportedIndex.current = landed;
      const target = offers[landed];
      if (target) onSlideChange?.(target.id);
    }
  };

  const scheduleSettle = () => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(reportSettled, SETTLE_DEBOUNCE_MS);
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || slotStep <= 0) return;
    // Coalesce to one read per frame — iOS fires scroll rapidly during
    // momentum and we only need the latest position per paint.
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const node = scrollRef.current;
        if (node) setScrollPos(node.scrollLeft / slotStep);
      });
    }
    scheduleSettle();
  };

  // Finger down/up on the gesture layer. The release re-arms the settle check
  // because a swipe can end exactly on a snap point, in which case no further
  // scroll event follows to schedule one.
  const handleTouchStart = () => {
    touchActive.current = true;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length > 0) return; // another finger still down
    touchActive.current = false;
    scheduleSettle();
  };

  // Desktop only: click-and-drag the slider horizontally with a mouse.
  // Native scroll containers don't drag with a mouse, so we translate the
  // pointer delta into `scrollLeft` ourselves. Touch is left entirely to the
  // browser (the `pointerType` guard) — that's what keeps the iOS fix intact.
  //
  // Listeners live on `window`, NOT the element: setting `scrollLeft` mid-drag
  // drops pointer capture in Chrome, so an element-level `pointerup` is missed
  // when the button is released OUTSIDE the slider — the drag would then stick
  // and resume when the cursor came back. Window always hears the release, and
  // the `buttons` check is a second safety net for a swallowed pointerup.
  const handleDragStart = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || len < 2) return;
    const el = scrollRef.current;
    if (!el) return;
    const startX = e.clientX;
    const startScrollLeft = el.scrollLeft;
    const step = slotStep;
    const controller = new AbortController();

    const finish = () => {
      controller.abort();
      if (settleTimer.current) clearTimeout(settleTimer.current);
      dragCleanupRef.current = null;
      setSnapEnabled(true);
      const node = scrollRef.current;
      if (node && step > 0) {
        scrollToIndex(Math.round(node.scrollLeft / step), true);
      }
    };
    const onMove = (ev: PointerEvent) => {
      if ((ev.buttons & 1) === 0) {
        finish();
        return;
      }
      el.scrollLeft = startScrollLeft - (ev.clientX - startX);
    };

    setSnapEnabled(false);
    dragCleanupRef.current = () => controller.abort();
    const opts = { signal: controller.signal };
    window.addEventListener('pointermove', onMove, opts);
    window.addEventListener('pointerup', finish, opts);
    window.addEventListener('pointercancel', finish, opts);
    e.preventDefault();
  };

  if (len === 0) {
    return (
      <div ref={containerRef} className="relative w-full select-none">
        <div className="relative w-full" style={{ height: dims.sliderHeight }} />
      </div>
    );
  }

  // Inner edge of each 36px chevron, so its centre lands on
  // `dims.chevronOffset`. On narrow desktop columns that would push the
  // buttons past the container edge — clamp so they stay fully visible,
  // overlapping the pack edge like a regular carousel when there's no
  // room beside it.
  const chevronEdge = dims.chevronOffset - CHEVRON_SIZE_PX / 2;
  const chevronOffset = containerW > 0 ? Math.min(chevronEdge, containerW / 2 - 48) : chevronEdge;

  const pedestal = isDesktop ? DESKTOP_PEDESTAL : phonePedestal(dims.pack);
  const sliderHeight = hasPedestal ? Math.max(dims.sliderHeight, pedestal.top + pedestal.h + 8) : dims.sliderHeight;

  return (
    <div ref={containerRef} className="relative w-full select-none">
      {/* Visual coverflow layer — non-interactive, driven by `scrollPos`.
          Clip the peek slots HORIZONTALLY so a side pack (esp. the right
          peek, which translateX's past the viewport) can't widen the document
          — a wider document makes the `fixed left-0 right-0` bottom bar span
          the whole scroll-width (e.g. 556px) and pushes its RIP/plateau
          off-screen. `clip` + default `overflow-y:visible` keeps vertical pack
          shadows intact (they coexist per spec). Desktop keeps `visible` —
          there the bottom bar is in-flow, not fixed, so no widening problem. */}
      <div
        className="relative w-full overflow-x-clip @[800px]:overflow-x-visible"
        style={{ height: sliderHeight }}
      >
        {/* HeroBackdrop (sonar waves) is rendered by PackPage one level
            up so it bleeds *behind* the header and *past* the slider's
            own bounds, matching Figma 543:2118. Slider itself only
            renders the slots over the wave layer. */}

        {/* Chevron arrows — desktop only. Positioned right next to the
            pack (40 px gap from each pack edge) instead of at the slider
            container edges, matching Figma 543:2118 where the arrows sit
            tight against the centre pack. */}
        {isDesktop && len > 1 && (
          <>
            <button
              type="button"
              onClick={() => scrollToIndex(currentIndex - 1, true)}
              disabled={currentIndex === 0}
              aria-label="Previous pack"
              style={{ left: `calc(50% - ${chevronOffset}px)` }}
              className="
                absolute top-1/2 -translate-y-1/2 -translate-x-full z-30
                flex h-9 w-9 items-center justify-center rounded-full
                text-white [background:var(--glass-panel-fill)]
                [backdrop-filter:blur(var(--glass-panel-blur))]
                border-[0.5px] border-solid [border-color:var(--glass-panel-rim)]
                cursor-pointer transition
                hover:brightness-125 hover:scale-105
                disabled:cursor-default disabled:opacity-30 disabled:hover:scale-100
              "
            >
              <ChevronLeft size={16} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollToIndex(currentIndex + 1, true)}
              disabled={currentIndex === len - 1}
              aria-label="Next pack"
              style={{ left: `calc(50% + ${chevronOffset}px)` }}
              className="
                absolute top-1/2 -translate-y-1/2 z-30
                flex h-9 w-9 items-center justify-center rounded-full
                text-white [background:var(--glass-panel-fill)]
                [backdrop-filter:blur(var(--glass-panel-blur))]
                border-[0.5px] border-solid [border-color:var(--glass-panel-rim)]
                cursor-pointer transition
                hover:brightness-125 hover:scale-105
                disabled:cursor-default disabled:opacity-30 disabled:hover:scale-100
              "
            >
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
          </>
        )}

        {offers.map((slotOffer, sourceIndex) => {
          // `scrollPos` is fractional, so `distance` is continuous — the peek
          // scales/slides smoothly as you scroll instead of stepping per pack.
          const distance = sourceIndex - scrollPos;
          if (Math.abs(distance) > MAX_RENDER_WINDOW + 0.5) return null;

          const slotOffsetPx = distance * slotStep;
          const centeredness = Math.max(0, 1 - Math.abs(distance));

          const packScale = lerp(dims.packPeekScale, 1, centeredness);
          const packOpacity = lerp(dims.packPeekOpacity, 1, centeredness);

          // On the desktop the peek pack shrinks about its centre; its
          // pedestal rises by as much to stay under it.
          const pedestalYShift = -(dims.pack.h / 2) * (1 - packScale);

          const slotZ = centeredness > 0.5 ? 20 : 10;

          return (
            <div
              key={`slot-${slotOffer.id}`}
              data-distance={distance}
              data-centeredness={centeredness.toFixed(2)}
              className="absolute inset-0"
              style={{
                pointerEvents: 'none',
                zIndex: slotZ,
              }}
              aria-hidden={centeredness < 0.9 || undefined}
            >
              {hasPedestal && (
                <div
                  className="absolute"
                  style={{
                    top: pedestal.top,
                    left: `calc(50% - ${pedestal.w / 2}px)`,
                    width: pedestal.w,
                    height: pedestal.h,
                    transform: `translateX(${slotOffsetPx}px) translateY(${pedestalYShift}px) scale(${lerp(pedestal.peekScale, 1, centeredness)})`,
                    opacity: lerp(0.3, 1, centeredness),
                  }}
                >
                  <Image
                    src={slotOffer.pedestalImageUrl ?? DEFAULT_PEDESTAL_URL}
                    alt=""
                    className="absolute inset-0"
                    imgClassName="absolute inset-0 h-full w-full object-contain"
                  />
                </div>
              )}

              {/* Pack — fixed px size, centred horizontally, peek
                  shifts down via translateY. */}
              {slotOffer.packImageUrl && (
                <div
                  className="absolute [filter:drop-shadow(0_20px_45px_rgba(0,0,0,0.45))]"
                  style={{
                    top: dims.pack.top,
                    left: `calc(50% - ${dims.pack.w / 2}px)`,
                    width: dims.pack.w,
                    height: dims.pack.h,
                    transform: `translateX(${slotOffsetPx}px) scale(${packScale})`,
                    transformOrigin: 'center center',
                    opacity: packOpacity,
                    transition: 'none',
                    // Explicit (not just inherited from the slot): on Android
                    // Chrome a `filter` layer can ignore an inherited
                    // pointer-events:none and steal the swipe from the scroll
                    // layer above. Setting it here keeps the pack un-hittable.
                    pointerEvents: 'none',
                  }}
                >
                  <Image
                    src={slotOffer.packImageUrl}
                    alt={centeredness > 0.9 ? slotOffer.title : ''}
                    className="absolute inset-0"
                    imgClassName="absolute inset-0 h-full w-full object-contain"
                    loading={centeredness > 0.5 ? 'eager' : 'lazy'}
                  />
                </div>
              )}

            </div>
          );
        })}
      </div>

      {/* Native horizontal-scroll gesture layer — transparent, sits on top
          of the coverflow and OWNS the swipe. The browser handles snap,
          momentum and axis-lock, so a vertical swipe falls through to page
          scroll (`touch-action: pan-x pan-y` + no vertical room here) and a
          horizontal swipe can't leak into a parent page's scroll
          (`overscroll-behavior-x: contain`) —
          the exact leak that made packs jump when the iframe sat inside a
          scrollable parent on iOS. Only invisible snap cells live here; the
          visible packs (with their drop-shadows) stay in the layer above.
          Chevrons (z-30) render above this layer, so their clicks still land. */}
      {len > 1 && (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          onPointerDown={handleDragStart}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          className="absolute inset-x-0 top-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{
            height: sliderHeight,
            zIndex: 25,
            scrollSnapType: snapEnabled ? 'x mandatory' : 'none',
            overscrollBehaviorX: 'contain',
            // Both axes allowed: horizontal is consumed by THIS scroller,
            // vertical has no room here (overflow-y:hidden) so it chains to
            // the page — i.e. you can still scroll the page by dragging on a
            // pack. `pan-x` alone would block that vertical drag entirely.
            touchAction: 'pan-x pan-y',
          }}
        >
          <div className="flex h-full w-max">
            <div aria-hidden style={{ flex: '0 0 auto', width: sidePad }} />
            {offers.map((snapOffer) => (
              <div
                key={`snap-${snapOffer.id}`}
                aria-hidden
                style={{
                  flex: '0 0 auto',
                  width: slotStep,
                  height: '100%',
                  scrollSnapAlign: 'center',
                }}
              />
            ))}
            <div aria-hidden style={{ flex: '0 0 auto', width: sidePad }} />
          </div>
        </div>
      )}

      {/* Phone: the active pack's title under the slider, one line
          (repackz-handoff §8.2). The desktop shows it in PackInfoCard. */}
      {!isDesktop && offers[currentIndex] && (
        <div data-co-component="PackTitle" className="px-4 text-center">
          <p
            className="truncate uppercase leading-[30px] tracking-[-0.62px]"
            style={{
              fontFamily: 'var(--co-PackTitle-fontFamily, Inter)',
              fontSize: 'var(--co-PackTitle-size, 22px)',
              fontWeight: 'var(--co-PackTitle-weight, 800)',
              color: 'var(--co-PackTitle-color, #ffffff)',
            }}
          >
            {offers[currentIndex].title}
          </p>
        </div>
      )}

      {/* Pagination dots — desktop only. On mobile the dots live in
          PackLayout's PotentialHits header (Figma 629:588), so the
          slider doesn't render any of its own below the pedestal.

          Active / idle colour flows through `--co-SliderDots-{active,idle}`
          so the Design Constructor (SliderDots component) can recolour
          every dot at once. Fallbacks are the original Tailwind tones
          (`bg-white` / `bg-white/40`), so an unmodified deploy stays
          pixel-identical. Hover state on idle dots is reproduced via
          a CSS custom-property nudge inside `onMouseEnter`/`Leave` —
          replacing the previous `hover:bg-white/60` arbitrary class. */}
      {len > 1 && (
        <div
          data-co-component="SliderDots"
          className="hidden @[800px]:flex items-center justify-center gap-[7px] pt-3"
        >
          {offers.map((o, i) => {
            const active = i === currentIndex;
            return (
              <button
                key={o.id}
                type="button"
                aria-label={`Go to ${o.title}`}
                aria-current={active || undefined}
                onClick={() => scrollToIndex(i, true)}
                className={
                  'rounded-full transition-all cursor-pointer ' +
                  (active ? 'h-2 w-2' : 'h-[6px] w-[6px]')
                }
                style={{
                  background: active
                    ? 'var(--co-SliderDots-active, rgba(255,255,255,1))'
                    : 'var(--co-SliderDots-idle, rgba(255,255,255,0.4))',
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
